import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  useCreateProjectLocationMutation,
  useUpdateProjectLocationMutation,
} from "@generated";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import AddressForm from "@components/Forms/AddressForm";
import ProjectVariables from "../../ProjectVariables/ProjectVariables";
import { Location, ProjectVariable } from "../types/Location";
import useStyles from "../styles/useStyles";
import { formatDate } from "@utils/helperFunctions";

interface Props {
  projectId: string;
  location?: Location;
  locations: Location[];
  projectVariableDefinitions: ProjectVariable[];
}

export interface LocationFormHandle {
  saveLocation: () => void;
}

const LocationForm = forwardRef<LocationFormHandle, Props>(
  ({ projectId, location, locations, projectVariableDefinitions }, ref) => {
    const { accountSlug } = useSelector((state: RootState) => state.slug);
    const navigate = useNavigate();
    const styles = useStyles();

    const isEdit = !!location;
    const today = new Date();

    // Address fields
    const [name, setName] = useState(location?.name || "");
    const [street, setStreet] = useState(location?.street || "");
    const [street2, setStreet2] = useState(location?.street2 || "");
    const [city, setCity] = useState(location?.city || "");
    const [state, setState] = useState(location?.state || "");
    const [postalCode, setPostalCode] = useState(location?.postalCode || "");
    const [country, setCountry] = useState(location?.country || "");
    const [countryId, setCountryId] = useState<string | null>(null);
    const [remote, setRemote] = useState(location?.remote ?? false);

    // Project variables
    const [requiredProjectVariables, setRequiredProjectVariables] = useState<
      ProjectVariable[]
    >([]);
    const [unrequiredProjectVariables, setUnrequiredProjectVariables] =
      useState<ProjectVariable[]>([]);

    // Alerts
    const [errorMessages, setErrorMessages] = useState("");
    const [showFailAlert, setShowFailAlert] = useState(false);

    const [createLocation] = useCreateProjectLocationMutation();
    const [updateLocation] = useUpdateProjectLocationMutation();

    useEffect(() => {
      if (isEdit && location) {
        setName(location.name);
        setStreet(location.street);
        setStreet2(location.street2);
        setCity(location.city);
        setState(location.state);
        setPostalCode(location.postalCode);
        setCountry(location.country);
        setRemote(location.remote);

        const pvs = location.projectVariables || [];
        setRequiredProjectVariables(
          pvs.filter((v) => v.required === true)
        );
        setUnrequiredProjectVariables(
          pvs.filter((v) => v.required === false)
        );
      } else if (!isEdit && projectVariableDefinitions.length > 0) {
        const reqVars = projectVariableDefinitions
          .filter((v) => v.required)
          .map((v) => ({
            ...v,
            value: v.variable_type === "date" ? formatDate(today) : "",
          }));
        const unreqVars = projectVariableDefinitions
          .filter((v) => !v.required)
          .map((v) => ({ ...v, value: "" }));
        setRequiredProjectVariables(reqVars);
        setUnrequiredProjectVariables(unreqVars);
      }
    }, [location, projectVariableDefinitions]);

    const validateForm = (): string[] => {
      const errors: string[] = [];

      if (!isEdit) {
        const duplicate = locations.some(
          (loc) => loc.name.toLowerCase() === name.toLowerCase()
        );
        if (duplicate) {
          errors.push(
            "Location name already exists. Please enter a different one."
          );
        }
      }

      if (!name.trim()) {
        errors.push("Project locations name can't be blank");
      }
      if (!remote) {
        if (!street.trim()) {
          errors.push("Project locations street can't be blank");
        }
        if (!city.trim()) {
          errors.push("Project locations city can't be blank");
        }
        if (!postalCode.trim()) {
          errors.push("Project locations postal code can't be blank");
        }
      }
      return errors;
    };

    const validateVariables = (
      allVars: ProjectVariable[]
    ): string | null => {
      for (const variable of allVars) {
        const varType = variable.variable_type;
        if (
          varType === "text" &&
          (!variable.select_options || variable.select_options.length === 0)
        ) {
          const val = variable.value || "";
          if (
            variable.minimum != null &&
            val.length < variable.minimum
          ) {
            return `Required text is too short (minimum is ${variable.minimum} characters)`;
          }
          if (
            variable.maximum != null &&
            val.length > variable.maximum
          ) {
            return `Required text is too long (maximum is ${variable.maximum} characters)`;
          }
        }
      }
      return null;
    };

    const saveLocation = async () => {
      const formErrors = validateForm();
      if (formErrors.length > 0) {
        setErrorMessages(formErrors.join(", "));
        setShowFailAlert(true);
        return;
      }

      const allVars = requiredProjectVariables.concat(
        unrequiredProjectVariables
      );
      const varError = validateVariables(allVars);
      if (varError) {
        setErrorMessages(varError);
        setShowFailAlert(true);
        return;
      }

      const projectVariablesPayload = allVars.map((v) => ({
        name: v.name,
        value: v.value,
      }));

      const payload = {
        data: {
          type: "project-locations" as const,
          ...(isEdit ? { id: Number(location!.id) } : {}),
          attributes: {
            name,
            street,
            street2,
            city,
            state,
            "postal-code": postalCode,
            country: countryId || country,
            remote,
            "project-variables": projectVariablesPayload,
          },
          relationships: {
            project: { data: { type: "projects", id: projectId } },
          },
        },
      };

      try {
        if (isEdit) {
          await updateLocation({
            slug: accountSlug,
            id: Number(location!.id),
            body: payload as any,
          }).unwrap();
          navigate(`/projects/${projectId}/project_locations`, {
            state: { edit: true },
          });
        } else {
          await createLocation({
            slug: accountSlug,
            body: payload as any,
          }).unwrap();
          navigate(`/projects/${projectId}/project_locations`, {
            state: { new: true },
          });
        }
      } catch (err: any) {
        const message =
          err?.data?.errors?.[0]?.detail ||
          "Something went wrong. Your changes could not be saved.";
        setErrorMessages(message);
        setShowFailAlert(true);
      }
    };

    useImperativeHandle(ref, () => ({ saveLocation }));

    return (
      <>
        {handleAlert(
          showFailAlert,
          errorMessages,
          setShowFailAlert,
          "warning",
          ToastAlert
        )}
        <Form>
          <AddressForm
            label="Location"
            placeholder="Customer Site"
            required={false}
            siteName={name}
            onSiteChange={(e: any) => setName(e.target.value)}
            remoteLocation={remote}
            onRemoteChanged={(e: any) => setRemote(e.target.checked)}
            addressText={street}
            onAddressChange={(e: any) => setStreet(e.target.value)}
            cityText={city}
            onCityChange={(e: any) => setCity(e.target.value)}
            stateText={state}
            onStateChange={(e: any) => setState(e.target.value)}
            zipText={postalCode}
            onZipChange={(e: any) => setPostalCode(e.target.value)}
            street2Text={street2}
            onStreet2Change={(e: any) => setStreet2(e.target.value)}
            country={country}
            onCountryChange={(e: any) => {
              setCountry(e);
              setCountryId(e.id);
            }}
          />
          <Row>
            <ProjectVariables
              arr={requiredProjectVariables}
              account_slug={accountSlug}
              project_id={projectId}
              setState={setRequiredProjectVariables}
              setVariableChanged={null}
            />
            <ProjectVariables
              arr={unrequiredProjectVariables}
              account_slug={accountSlug}
              project_id={projectId}
              setState={setUnrequiredProjectVariables}
              setVariableChanged={null}
            />
          </Row>
        </Form>
      </>
    );
  }
);

LocationForm.displayName = "LocationForm";

export default LocationForm;
